<template>
  <div v-if="editor" class="tip-tap-editor">
    <editor-content :editor="editor" class="editor-content" />
    <cta-row
      overlay-modal
      no-spacing
      classes="tip-tap-editor__controls-wrapper bg-white border-bottom"
    >
      <div class="tip-tap-editor__controls container w-100 py-1 px-2">
        <v-button
          x-small
          secondary
          :disabled="disabledBold"
          :active="editor.isActive('bold')"
          @click="toggleBold"
        >
          <v-icon icon="format_bold" />
        </v-button>
        <v-button
          x-small
          secondary
          :disabled="disabledItalic"
          :active="editor.isActive('italic')"
          @click="toggleItalic"
        >
          <v-icon icon="format_italic" />
        </v-button>
        <v-button
          x-small
          secondary
          :active="editor.isActive('bulletList')"
          @click="toggleBulletList"
        >
          <v-icon icon="format_list_bulleted" />
        </v-button>
        <v-button
          x-small
          secondary
          :active="editor.isActive('orderedList')"
          @click="toggleOrderedList"
        >
          <v-icon icon="format_list_numbered" />
        </v-button>
        <v-button x-small secondary @click="chooseFile">
          <v-icon icon="image" />
        </v-button>
        <input
          multiple
          class="d-none"
          ref="tipTapInput"
          type="file"
          accept="image/*"
          @input="e => addFiles(e)"
        />
      </div>
    </cta-row>
  </div>
</template>

<script>
import { Editor, EditorContent } from "@tiptap/vue-2"
import { Image as BaseImage } from "@tiptap/extension-image"
import Paragraph from "@tiptap/extension-paragraph"
import Document from "@tiptap/extension-document"
import Text from "@tiptap/extension-text"
import Bold from "@tiptap/extension-bold"
import Italic from "@tiptap/extension-italic"
import Link from "@tiptap/extension-link"
import OrderedList from "@tiptap/extension-ordered-list"
import BulletList from "@tiptap/extension-bullet-list"
import ListItem from "@tiptap/extension-list-item"
import Dropcursor from "@tiptap/extension-dropcursor"
import Placeholder from "@tiptap/extension-placeholder"

import ImageUpload from "@/lib/mixins/image-upload"

import CtaRow from "@/components/CtaRow"

const Image = BaseImage.extend({
  inline: true,
  selectable: true,
  atom: true,
  addAttributes() {
    return {
      key: {
        default: null
      },

      src: {
        default: null
      },

      alt: {
        default: null
      },

      uploading: {
        default: false
      },

      style: {
        default: null
      }
    }
  }
})

export default {
  mixins: [ImageUpload],

  components: {
    EditorContent,
    CtaRow
  },

  props: {
    content: { type: String, default: null },
    placeholder: { type: String, default: "" }
  },

  data() {
    return {
      editor: null,
      uploadingFiles: []
    }
  },

  computed: {
    disabledBold() {
      return !this.editor
        .can()
        .chain()
        .focus()
        .toggleBold()
        .run()
    },

    disabledItalic() {
      return !this.editor
        .can()
        .chain()
        .focus()
        .toggleItalic()
        .run()
    }
  },

  mounted() {
    this.editor = new Editor({
      content: this.content,
      extensions: [
        Paragraph,
        Document,
        Text,
        Bold,
        Italic,
        OrderedList,
        BulletList,
        ListItem,
        Dropcursor,
        Link,
        Image.configure({
          inline: true
        }),
        Placeholder.configure({
          placeholder: this.placeholder
        })
      ],
      parseOptions: {
        preserveWhitespace: "full"
      },
      onUpdate: ({ editor }) => {
        const html = editor.getHTML()

        this.$emit("input", this.isEmptyContent(html) ? "" : html)
      }
    })
  },

  beforeDestroy() {
    this.editor.destroy()
  },

  watch: {
    "uploadingFiles.length": {
      immediate: false,
      handler: function(value) {
        this.$emit("uploading", value > 0)
      }
    }
  },

  methods: {
    isEmptyContent(html) {
      let node = document.createElement("div")
      node.innerHTML = html

      return !this.hasVisibleContent(node)
    },

    hasVisibleContent(node) {
      // Check if node is an image element
      if (
        node.nodeType === Node.ELEMENT_NODE &&
        node.tagName.toLowerCase() === "img"
      ) {
        return true
      }
      // Check if node is a text node with non-whitespace text
      if (node.nodeType === Node.TEXT_NODE && node.textContent.trim() !== "") {
        return true
      }
      // If node has child nodes, check them recursively
      for (let i = 0; i < node.childNodes.length; i++) {
        if (this.hasVisibleContent(node.childNodes[i])) {
          return true
        }
      }
      return false
    },

    toggleBold() {
      this.editor
        .chain()
        .focus()
        .toggleBold()
        .run()
    },

    toggleItalic() {
      this.editor
        .chain()
        .focus()
        .toggleItalic()
        .run()
    },

    toggleBulletList() {
      this.editor
        .chain()
        .focus()
        .toggleBulletList()
        .run()
    },

    toggleOrderedList() {
      this.editor
        .chain()
        .focus()
        .toggleOrderedList()
        .run()
    },

    chooseFile() {
      this.$refs.tipTapInput.click()
    },

    async addFiles(e) {
      const files = Object.values(e.target.files)
      let validFiles = []
      this.$refs.tipTapInput.value = ""

      files.forEach((file, index) => {
        const validation = this.validateFile(file, false)

        if (!validation.valid) {
          this.$notify(`${validation.errorMessage}: ${file?.name}`)

          return
        }

        file.key = +new Date() + index
        validFiles.push(file)
      })

      this.uploadingFiles.push(...validFiles)

      validFiles.forEach(file => {
        // Show uploading placeholder
        this.editor
          .chain()
          .focus()
          .setImage({
            style: `background-image: url(${require("@/assets/images/loader.svg")}), url(${this.blobUrl(
              file
            )})`,
            key: file.key,
            uploading: true
          })
          .run()
      })

      for (const file of validFiles) {
        try {
          const uploadedImage = await this.uploadFile(file)

          this.replaceImage(file.key, uploadedImage)
        } catch {
          this.removeImage(file.key)
          this.$notify(`Upload Failed: ${file.name}`)
        } finally {
          this.uploadingFiles = this.uploadingFiles.filter(f => f != file)
        }
      }
    },

    findImagePosition(key) {
      let imagePos = null

      this.editor.state.doc.descendants((node, pos) => {
        if (node.type.name === "image" && node.attrs.key === key) {
          imagePos = pos

          return false
        }
      })

      return imagePos
    },

    findImageNode(position) {
      return this.editor.view.state.doc.nodeAt(position)
    },

    removeImage(key) {
      const replacePosition = this.findImagePosition(key)
      let imageNode = this.findImageNode(replacePosition)

      if (imageNode && imageNode.type.name === "image") {
        let { tr } = this.editor.view.state
        let endPosition = replacePosition + imageNode.nodeSize

        tr.delete(replacePosition, endPosition)
        this.editor.view.dispatch(tr)

        return replacePosition
      }

      return false
    },

    replaceImage(key, file) {
      const replacePosition = this.removeImage(key)

      if (replacePosition) {
        let { tr } = this.editor.view.state
        let newImageNode = this.editor.schema.nodes.image.create({
          src: file.path,
          alt: file.filename
        })

        tr.insert(replacePosition, newImageNode)

        this.editor.view.dispatch(tr)
      }
    }
  }
}
</script>

<style lang="scss">
@import "@/assets/styles/variables";

.ProseMirror {
  > * {
    margin-top: 0.5rem;
    margin-bottom: 0.5rem;
  }

  img {
    max-width: 100%;
    height: auto;

    &[uploading="true"] {
      display: block;
      background-color: transparent;
      background-size: 50px, contain;
      background-repeat: no-repeat;
      background-position: center;
      width: 300px;
      height: 200px;
      opacity: 0.5;
    }

    &.ProseMirror-selectednode {
      outline: 1px solid var(--brand-color-hex);
    }
  }
}

.ProseMirror p.is-editor-empty:first-child::before {
  content: attr(data-placeholder);
  float: left;
  color: $color-ui--grey-50;
  pointer-events: none;
  height: 0;
}
</style>

<style lang="scss">
@import "@/assets/styles/variables";

.tip-tap-editor__controls-wrapper {
  margin-bottom: calc(
    68px + env(safe-area-inset-bottom)
  ); // Height of cta row actions

  .v-cta-row__content {
    padding-left: 0;
    padding-right: 0;
  }
}

.tip-tap-editor__controls {
  border-top: 1px solid $color-ui--grey-20;
  background-color: $color-ui--white;
  opacity: 0;
  animation: fade-in 0.2s ease forwards 0.4s;

  .v-button {
    margin-right: 0.25rem;
    font-size: 15px;
    padding: 14px;

    &.--active {
      opacity: 0.3;
    }

    .v-icon {
      color: $color-ui--black;
    }
  }
}
</style>
